import { Reveal } from "./Motion";

/**
 * Editorial specifications section for a product page.
 * If a `spec.image` is provided, render the photorealistic bowl photo
 * scaled to reflect the relative height of each size (visually proportional
 * within the row). Falls back to a technical SVG diagram if no image.
 * Below each card, a 4-column facts strip (Capacity · Height · Top · Bottom)
 * mirrors the buyer's reference layout.
 */
export default function SpecSection({ specs, unit = "mm", title = "Sizes & specifications" }) {
    if (!specs || specs.length === 0) return null;

    const maxH = Math.max(...specs.map(s => s.height));
    const useImages = specs.every(s => !!s.image);

    return (
        <section className="bg-sand border-y border-forest" data-testid="spec-section">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
                <Reveal>
                    <div className="grid lg:grid-cols-12 gap-8 mb-16">
                        <div className="lg:col-span-4 font-mono text-xs uppercase tracking-widest text-clay">[ Size Range & Specifications ]</div>
                        <h2 className="lg:col-span-8 font-serif text-5xl lg:text-7xl leading-[1] tracking-tight text-forest">
                            {title.split(" ").slice(0, -1).join(" ")} <em className="italic">{title.split(" ").slice(-1)}</em>
                        </h2>
                    </div>
                </Reveal>

                <div className={`grid grid-cols-1 sm:grid-cols-2 ${specs.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-6 lg:gap-8 mb-16 items-end`}>
                    {specs.map((s, i) => {
                        // Card visual height scales with real bowl height so the row reads at a glance
                        const heightPct = 55 + (s.height / maxH) * 45; // 55%-100% of viz area
                        return (
                            <Reveal key={s.size} delay={i * 0.05}>
                                <div className="bg-bone border border-forest/20 group hover:border-forest transition-colors overflow-hidden" data-testid={`spec-bowl-${s.size.replace(/\s+/g, '-')}`}>
                                    {/* Photo panel */}
                                    <div className="relative bg-bone" style={{ paddingBottom: "88%" }}>
                                        {useImages ? (
                                            <div className="absolute inset-0 flex items-end justify-center px-4 pb-2">
                                                <img
                                                    src={s.image}
                                                    alt={`${s.size} kraft paper bowl — top ${s.top}mm, height ${s.height}mm, base ${s.bottom}mm`}
                                                    style={{ height: `${heightPct}%`, width: "auto", maxWidth: "100%", objectFit: "contain" }}
                                                    loading="lazy"
                                                />
                                            </div>
                                        ) : (
                                            <SvgBowl s={s} maxTop={Math.max(...specs.map(x => x.top))} maxH={maxH} />
                                        )}
                                        {/* baseline shadow line for visual grounding */}
                                        <div className="absolute left-6 right-6 bottom-2 h-px bg-forest/15" aria-hidden="true" />
                                    </div>

                                    {/* Facts strip — 4-col */}
                                    <div className="border-t border-forest/20 grid grid-cols-4 divide-x divide-forest/15 text-center">
                                        <Fact k="Capacity" v={s.size} />
                                        <Fact k="Height" v={`${s.height} ${unit}`} />
                                        <Fact k="Top ⌀" v={`${s.top} ${unit}`} />
                                        <Fact k="Base ⌀" v={`${s.bottom} ${unit}`} />
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                {/* Specs table */}
                <div className="border border-forest overflow-x-auto" data-testid="spec-table">
                    <table className="w-full text-sm">
                        <thead className="bg-forest text-bone">
                            <tr>
                                <th className="text-left px-6 py-4 font-mono text-xs uppercase tracking-widest">Size</th>
                                <th className="text-left px-6 py-4 font-mono text-xs uppercase tracking-widest">Dimensions</th>
                                <th className="text-left px-6 py-4 font-mono text-xs uppercase tracking-widest">Lid Compatibility</th>
                                <th className="text-left px-6 py-4 font-mono text-xs uppercase tracking-widest">GSM / Micron</th>
                            </tr>
                        </thead>
                        <tbody>
                            {specs.map((s, i) => (
                                <tr key={s.size} className={i % 2 === 0 ? "bg-bone" : "bg-sand"}>
                                    <td className="px-6 py-5 font-serif text-2xl text-forest">{s.size}</td>
                                    <td className="px-6 py-5 text-inkgreen font-mono text-[13px]">Top {s.top}{unit} · Height {s.height}{unit} · Bottom {s.bottom}{unit}</td>
                                    <td className="px-6 py-5 text-inkgreen">{s.lid}</td>
                                    <td className="px-6 py-5 text-inkgreen font-mono text-[13px]">{s.gsm}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

function Fact({ k, v }) {
    return (
        <div className="py-4 px-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-stone">{k}</div>
            <div className="mt-1 font-serif text-forest text-lg leading-tight">{v}</div>
        </div>
    );
}

function SvgBowl({ s, maxTop, maxH }) {
    const W = 300, H = 260, PAD_T = 40, PAD_B = 40, MAX_W = 200;
    const scale = Math.min(MAX_W / maxTop, (H - PAD_T - PAD_B) / maxH);
    const topPx = s.top * scale, botPx = s.bottom * scale, hPx = s.height * scale;
    const CX = W / 2, topRx = topPx / 2, topRy = topRx * 0.22, botRx = botPx / 2, botRy = botRx * 0.22;
    const yBase = H - PAD_B, yTop = yBase - hPx, bez = hPx * 0.15;
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full">
            <ellipse cx={CX} cy={yBase + 8} rx={botRx * 1.15} ry={botRy * 1.3} fill="#0A2515" opacity="0.10" />
            <path d={`M ${CX - botRx} ${yBase} Q ${CX - topRx - bez} ${yTop + hPx * 0.55} ${CX - topRx} ${yTop} A ${topRx} ${topRy} 0 0 0 ${CX + topRx} ${yTop} Q ${CX + topRx + bez} ${yTop + hPx * 0.55} ${CX + botRx} ${yBase} A ${botRx} ${botRy} 0 0 1 ${CX - botRx} ${yBase} Z`} fill="#D6C29B" stroke="#123A24" strokeWidth="1.2" />
            <ellipse cx={CX} cy={yTop} rx={topRx} ry={topRy} fill="none" stroke="#123A24" strokeWidth="1.2" />
        </svg>
    );
}
